import { Alert } from '@tempmee/react';
import { OutlineRefreshIcon } from '@tempmee/react-icons';
import type { IAuthorization } from '~/domain/Credentials/types/Credentials';
import { IJob, IUser } from '~/models';
import { BookedProfessionalDetails } from '../../BookedProfessionalDetails';
import { BookedProfessionalPanel } from '../../BookedProfessionalPanel';

export interface ICancelledProfessionalPanelProps {
  job: Partial<IJob> | null | undefined;
  /** The professional who held the shift — `job.offer_final_reciever`. */
  professional: IUser;
  /**
   * Whether the professional was the one who cancelled.
   *
   * Decides what sits under the topper: their reposted notice, or their full
   * profile. Passed in rather than derived here so one place owns the variant
   * rule — see `resolveCancelledExpiredVariant`.
   */
  isCancelledByProfessional: boolean;
  /**
   * Whether the shift was actually reposted, which is a narrower question than
   * who cancelled — see `wasRepostedAfterCancellation`. When the professional
   * cancelled something the backend does not repost (an onsite interview, a
   * dual-model shift) the notice is omitted rather than claiming a repost that
   * never happened.
   */
  wasReposted: boolean;
  /** Verified credentials for the Credentials tab. */
  authorizations?: IAuthorization[] | null;
  /**
   * Whether the shell has stacked its columns.
   *
   * Only `CandidateDetails` still needs telling: it has no container query, so
   * the two places the design restructures rather than reflows (the summary
   * row's divider direction and the badge size) are a prop. The topper answers
   * to its own width.
   */
  isCollapsed?: boolean;
}

/**
 * The primary column for a terminal shift that had a professional booked on it.
 *
 * One panel, two bodies, because the two Figma frames differ only in what sits
 * under the topper:
 *
 * - the professional cancelled → the reposted notice, and nothing else. Their
 *   profile is no longer the story, and the legacy screen showed the same single
 *   sentence in place of the whole summary. The notice itself is conditional:
 *   the backend does not repost interviews or dual-model shifts, so those show
 *   the topper alone rather than a repost that never happened. The Figma has no
 *   frame for that case — raised with design.
 * - the office (or GoTu) cancelled → the professional's full profile, so the
 *   office has the record of who they cancelled on.
 *
 * The header is the design system's `CandidateTopper` under its
 * `complete-cancelled` style, so this professional shows the same signals here
 * as on the list they were picked from. It is handed no `actions` and no
 * `overflowMenu`: this is a terminal state, and the legacy screen gates
 * favourite/block on `status !== 'cancelled'` for the same reason. The Figma
 * draws a `⋯` trigger with no menu behind it — raised with design rather than
 * filled with invented items.
 *
 * Floored at 320px like the single-candidate page, and for the same reason: a
 * cancelled professional's tabs carry wildly different amounts, so without it
 * the card jumps as the office switches and an empty Procedures tab reads as a
 * broken page. dental-portal has no Tailwind build, so only the arbitrary values
 * the design system's own stylesheet already emits resolve — `min-h-[320px]` is
 * one of them, which is why that is the number rather than the Figma's ~660px.
 */
export const CancelledProfessionalPanel = ({
  job,
  professional,
  isCancelledByProfessional,
  wasReposted,
  authorizations,
  isCollapsed = false,
}: ICancelledProfessionalPanelProps) => {
  /**
   * What sits under the topper, or `null` when there is nothing to say — a
   * professional cancelling something the backend does not repost.
   *
   * Resolved before the layout so the two mutually exclusive bodies read as one
   * decision rather than a ternary nested inside the JSX.
   */
  const body = isCancelledByProfessional ? (
    wasReposted ? (
      <Alert
        severity="info"
        /*
         * The frame's glyph is `Arrow_refresh`, not the severity's own info
         * circle: it says what happened to the shift rather than that there is
         * something worth reading. `OutlineRepostIcon` is the wrong one — that
         * is the rounded box with an arrow.
         *
         * No size prop: the block's icon slot pins any child svg to 24px
         * (`[&_svg]:size-6`), which is the frame's size.
         */
        icon={<OutlineRefreshIcon />}
        /*
         * `title`, not `description`. The Figma binds this copy to the
         * `alert/title` style — Bold 16 — and the block splits the two on
         * exactly that: `title` renders `font-bold`, `description`
         * `font-normal`. There is no second line here, so the one line is the
         * title.
         */
        title="This shift has been reposted"
        data-testid="shift-reposted-alert"
        data-cy="shift-reposted-alert"
      />
    ) : null
  ) : (
    <BookedProfessionalDetails
      job={job}
      professional={professional}
      authorizations={authorizations}
      isCollapsed={isCollapsed}
    />
  );

  /*
   * Composed from `BookedProfessionalPanel` rather than rebuilt: the shell — the
   * panel surface, the topper and its identity — is the same one the scheduled
   * and completed pages draw. Only the body differs, which is what the slot is
   * for.
   */
  return (
    <BookedProfessionalPanel
      job={job}
      professional={professional}
      authorizations={authorizations}
      topperStyle="complete-cancelled"
      testIDPrefix="cancelled"
      isCollapsed={isCollapsed}
      className="min-h-[320px]"
    >
      {body}
    </BookedProfessionalPanel>
  );
};
